import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  FilterList,
  Menu as MenuIcon,
  Refresh,
  Delete as DeleteIcon,
  DeleteSweepOutlined,
  ViewModule as ViewModuleIcon,
  TableRows as TableRowsIcon,
  NotificationsOutlined,
  AutoAwesomeOutlined,
  MovieOutlined,
} from '@mui/icons-material';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger/Logger';

const SYSTEM_QUEUE_PATH = '/administration/system-queue';
const VIEWMODE_STORAGE_KEY = 'system-queue-view-mode';
const QUEUE_STORAGE_KEY = 'system-queue-selected-queue';

type QueueType = 'openai-prompt' | 'notifications' | 'reel-export';
const QUEUE_TYPES: QueueType[] = ['openai-prompt', 'notifications', 'reel-export'];
type ViewMode = 'tile' | 'table';

interface OpenAiQueueItem {
  jobId: string;
  promptId: string;
  title: string;
  projectName: string;
  companyId: string;
  repoId: string;
  status: 'in-progress' | 'scheduled';
  owner: string | null;
  visibleAt: number;
  enqueuedOrder: number;
}

interface NotificationsQueueItem {
  jobId: string;
  auditEventType: string;
  auditEventDescription: string;
  createdBy: string;
  createdAt: string;
  applicationServerIp: string;
  companyId: string;
  enqueuedAt?: number;
  status?: 'in-progress' | 'scheduled';
  owner?: string | null;
  visibleAt?: number;
  enqueuedOrder?: number;
}

interface ReelExportQueueItem {
  jobId: string;
  status: 'in-progress' | 'scheduled';
  owner: string | null;
  visibleAt: number;
  enqueuedAt: number;
  enqueuedOrder: number;
  reelId: string;
  reelName: string;
  versionLabel: string;
  width: number;
  height: number;
  durationMs: number;
  companyId: string;
  companyName: string;
  requestedBy: string;
  s3Uri: string;
  outputKey: string;
  locationLabel: string;
  progress: {
    stage: string;
    percent: number;
    message?: string;
    worker: string;
    updatedAt: number;
  } | null;
}

const QUEUE_OPTIONS: { value: QueueType; label: string }[] = [
  { value: 'openai-prompt', label: 'OpenAI Prompt' },
  { value: 'notifications', label: 'Notifications Queue' },
  { value: 'reel-export', label: 'Reel Export (Beam on Screen)' },
];

const formatDuration = (ms?: number) => {
  if (!ms || !Number.isFinite(ms)) return '—';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatTimestamp = (value?: string | number) => {
  if (value == null || value === '') return '—';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch {
    return String(value);
  }
};

export const SystemQueue: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [queueType, setQueueType] = useState<QueueType>(() => {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY) as QueueType | null;
    return stored && QUEUE_TYPES.includes(stored) ? stored : 'openai-prompt';
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [openAiItems, setOpenAiItems] = useState<OpenAiQueueItem[]>([]);
  const [notificationItems, setNotificationItems] = useState<NotificationsQueueItem[]>([]);
  const [reelExportItems, setReelExportItems] = useState<ReelExportQueueItem[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [selectedOpenAiItem, setSelectedOpenAiItem] = useState<OpenAiQueueItem | null>(null);
  const [selectedNotificationItem, setSelectedNotificationItem] = useState<NotificationsQueueItem | null>(null);
  const [selectedReelExportItem, setSelectedReelExportItem] = useState<ReelExportQueueItem | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  useEffect(() => {
    localStorage.setItem(QUEUE_STORAGE_KEY, queueType);
  }, [queueType]);

  useEffect(() => {
    localStorage.setItem(VIEWMODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    const fetchQueue = async () => {
      try {
        setLoading(true);
        const resp = await apiClient.get(`/administration/system-queue?queue=${queueType}`);
        const data = (resp as any)?.data ?? resp;
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        if (queueType === 'notifications') {
          setNotificationItems(items);
          setOpenAiItems([]);
          setReelExportItems([]);
        } else if (queueType === 'reel-export') {
          setReelExportItems(items);
          setOpenAiItems([]);
          setNotificationItems([]);
        } else {
          setOpenAiItems(items);
          setNotificationItems([]);
          setReelExportItems([]);
        }
      } catch (e: any) {
        Logger.error('Administration/SystemQueue', `Failed to fetch queue: ${e?.message || ''}`);
        setOpenAiItems([]);
        setNotificationItems([]);
        setReelExportItems([]);
        showSnackbar(e?.message || 'Failed to load queue', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchQueue();
  }, [refreshKey, queueType, showSnackbar]);

  const filteredOpenAiItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return openAiItems;
    return openAiItems.filter((item) =>
      [item.title, item.projectName, item.promptId, item.jobId, item.owner]
        .some((field) => String(field || '').toLowerCase().includes(term)),
    );
  }, [openAiItems, search]);

  const filteredNotificationItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return notificationItems;
    return notificationItems.filter((item) =>
      [
        item.jobId,
        item.auditEventType,
        item.auditEventDescription,
        item.createdBy,
        item.companyId,
        item.applicationServerIp,
        item.owner,
      ].some((field) => String(field || '').toLowerCase().includes(term)),
    );
  }, [notificationItems, search]);

  const filteredReelExportItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reelExportItems;
    return reelExportItems.filter((item) =>
      [
        item.jobId,
        item.reelName,
        item.versionLabel,
        item.companyName,
        item.companyId,
        item.requestedBy,
        item.s3Uri,
        item.owner,
        item.progress?.stage,
      ].some((field) => String(field || '').toLowerCase().includes(term)),
    );
  }, [reelExportItems, search]);

  const isEmpty = queueType === 'openai-prompt'
    ? filteredOpenAiItems.length === 0
    : queueType === 'reel-export'
      ? filteredReelExportItems.length === 0
      : filteredNotificationItems.length === 0;

  const toolbarSurfaceSx = {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: { xs: 1, md: 2 },
    alignItems: 'center',
    mb: 3,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
  };

  const splitExporter = (owner: string | null | undefined): { host: string; ip: string } => {
    if (!owner) return { host: '—', ip: '—' };
    const at = owner.lastIndexOf('@');
    if (at < 0) return { host: owner, ip: '—' };
    return { host: owner.slice(0, at) || '—', ip: owner.slice(at + 1) || '—' };
  };

  const renderStatusChip = (status: OpenAiQueueItem['status']) => {
    if (status === 'in-progress') {
      return <Chip label="In progress" color="info" variant="outlined" size="small" />;
    }
    return <Chip label="Scheduled" color="warning" variant="outlined" size="small" />;
  };

  const handleOpenAiDeleteClick = (item: OpenAiQueueItem) => {
    setSelectedOpenAiItem(item);
    setSelectedNotificationItem(null);
    setDeleteOpen(true);
  };

  const handleNotificationDeleteClick = (item: NotificationsQueueItem) => {
    setSelectedNotificationItem(item);
    setSelectedOpenAiItem(null);
    setSelectedReelExportItem(null);
    setDeleteOpen(true);
  };

  const handleReelExportDeleteClick = (item: ReelExportQueueItem) => {
    setSelectedReelExportItem(item);
    setSelectedOpenAiItem(null);
    setSelectedNotificationItem(null);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (queueType === 'openai-prompt' && selectedOpenAiItem) {
        await apiClient.delete(
          `/administration/system-queue/item/${selectedOpenAiItem.jobId}?queue=openai-prompt`,
        );
      } else if (queueType === 'notifications' && selectedNotificationItem) {
        await apiClient.delete(
          `/administration/system-queue/item/${encodeURIComponent(selectedNotificationItem.jobId)}?queue=notifications`,
        );
      } else if (queueType === 'reel-export' && selectedReelExportItem) {
        await apiClient.delete(
          `/administration/system-queue/item/${encodeURIComponent(selectedReelExportItem.jobId)}?queue=reel-export`,
        );
      } else {
        setDeleteOpen(false);
        return;
      }
      setDeleteOpen(false);
      setSelectedOpenAiItem(null);
      setSelectedNotificationItem(null);
      setSelectedReelExportItem(null);
      showSnackbar('Queue item deleted');
      setRefreshKey((x) => x + 1);
    } catch (e: any) {
      Logger.error('Administration/SystemQueue', `Failed to delete item: ${e?.message || ''}`);
      showSnackbar(e?.message || 'Failed to delete queue item', 'error');
      setDeleteOpen(false);
    }
  };

  const confirmClear = async () => {
    try {
      const resp = await apiClient.delete(`/administration/system-queue?queue=${queueType}`);
      const removed = (resp as any)?.data?.removed ?? (resp as any)?.removed ?? 0;
      setClearOpen(false);
      showSnackbar(
        queueType === 'openai-prompt'
          ? `Cleared ${removed} scheduled item(s)`
          : queueType === 'reel-export'
            ? `Cleared ${removed} scheduled export(s)`
            : `Cleared ${removed} notification item(s)`,
      );
      setRefreshKey((x) => x + 1);
    } catch (e: any) {
      Logger.error('Administration/SystemQueue', `Failed to clear queue: ${e?.message || ''}`);
      showSnackbar(e?.message || 'Failed to clear queue', 'error');
      setClearOpen(false);
    }
  };

  const renderQueueSelector = (fullWidth = false) => (
    <FormControl size="small" sx={{ minWidth: fullWidth ? '100%' : 200, flexShrink: 0 }}>
      <InputLabel id="system-queue-type-label">Queue</InputLabel>
      <Select
        labelId="system-queue-type-label"
        label="Queue"
        value={queueType}
        onChange={(e) => setQueueType(e.target.value as QueueType)}
      >
        {QUEUE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderViewToggle = () => (
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      size="small"
      onChange={(_, value: ViewMode | null) => {
        if (value) setViewMode(value);
      }}
      aria-label="view mode"
    >
      <ToggleButton value="tile" aria-label="tile view">
        <ViewModuleIcon fontSize="small" />
      </ToggleButton>
      <ToggleButton value="table" aria-label="table view">
        <TableRowsIcon fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const renderActionButtons = (variant: 'desktop' | 'mobile' = 'desktop') => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'nowrap',
        flexShrink: 0,
        ...(variant === 'mobile' ? { flexDirection: 'column', alignItems: 'stretch', width: '100%' } : {}),
      }}
    >
      {variant === 'mobile' && renderQueueSelector(true)}
      {variant === 'desktop' && renderQueueSelector()}
      {renderViewToggle()}
      <Tooltip title="Refresh">
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setRefreshKey((x) => x + 1);
            if (variant === 'mobile') setToolbarMenuOpen(false);
          }}
          size="small"
          sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
          aria-label="Refresh"
        >
          <Refresh fontSize="small" />
        </Button>
      </Tooltip>
      <Tooltip title={queueType === 'notifications' ? 'Clear queue' : 'Clear scheduled items'}>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setClearOpen(true);
            if (variant === 'mobile') setToolbarMenuOpen(false);
          }}
          size="small"
          sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
          aria-label="Clear queue"
        >
          <DeleteSweepOutlined fontSize="small" />
        </Button>
      </Tooltip>
    </Box>
  );

  const renderOpenAiTile = (item: OpenAiQueueItem) => {
    const isInProgress = item.status === 'in-progress';
    return (
      <Paper
        key={item.jobId}
        sx={{
          borderRadius: '8px',
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 160,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <AutoAwesomeOutlined color="secondary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.title || '(untitled)'}
            </Typography>
            {renderStatusChip(item.status)}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Project: {item.projectName || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Prompt ID: {item.promptId || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Job ID: {item.jobId}
          </Typography>
          {isInProgress && item.owner && (
            <Typography variant="body2" color="text.secondary">
              Owner: {item.owner}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Visible at: {formatTimestamp(item.visibleAt)}
          </Typography>
        </Box>
        <Box>
          {isInProgress ? (
            <Tooltip title="Cannot delete while in progress">
              <span>
                <IconButton disabled aria-label="delete disabled">
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleOpenAiDeleteClick(item)} aria-label="delete">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
    );
  };

  const renderNotificationTile = (item: NotificationsQueueItem) => {
    const isInProgress = item.status === 'in-progress';
    return (
    <Paper
      key={item.jobId}
      sx={{
        borderRadius: '8px',
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        minHeight: 160,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <NotificationsOutlined color="secondary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {item.auditEventType || '(no event type)'}
          </Typography>
          {item.status && renderStatusChip(item.status)}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Created by: {item.createdBy || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Company: {item.companyId || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Server: {item.applicationServerIp || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          Job ID: {item.jobId}
        </Typography>
        {isInProgress && item.owner && (
          <Typography variant="body2" color="text.secondary">
            Owner: {item.owner}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {item.auditEventDescription || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Created at: {formatTimestamp(item.createdAt)}
        </Typography>
      </Box>
      <Box>
        {isInProgress ? (
          <Tooltip title="Cannot delete while in progress">
            <span>
              <IconButton disabled aria-label="delete disabled">
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Delete">
            <IconButton color="error" onClick={() => handleNotificationDeleteClick(item)} aria-label="delete">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
    );
  };

  const renderReelExportProgress = (item: ReelExportQueueItem) => {
    if (!item.progress || item.status !== 'in-progress') return null;
    const pct = Math.max(0, Math.min(100, Number(item.progress.percent) || 0));
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {item.progress.stage}{item.progress.message ? ` — ${item.progress.message}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">{pct}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} color="info" sx={{ borderRadius: 1, height: 6 }} />
      </Box>
    );
  };

  const renderReelExportTile = (item: ReelExportQueueItem) => {
    const isInProgress = item.status === 'in-progress';
    return (
      <Paper
        key={item.jobId}
        sx={{
          borderRadius: '8px',
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 160,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <MovieOutlined color="secondary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.reelName || '(unknown reel)'}
            </Typography>
            {item.versionLabel && <Chip label={item.versionLabel} size="small" variant="outlined" />}
            {renderStatusChip(item.status)}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {item.width}×{item.height} · {formatDuration(item.durationMs)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Company: {item.companyName || item.companyId || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Requested by: {item.requestedBy || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Output: {item.s3Uri || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Job ID: {item.jobId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Exporter host: {isInProgress ? splitExporter(item.owner).host : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Exporter IP: {isInProgress ? splitExporter(item.owner).ip : '—'}
          </Typography>
          {renderReelExportProgress(item)}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Queued at: {formatTimestamp(item.enqueuedAt)}
          </Typography>
        </Box>
        <Box>
          {isInProgress ? (
            <Tooltip title="Cannot delete while an exporter is processing this item">
              <span>
                <IconButton disabled aria-label="delete disabled">
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleReelExportDeleteClick(item)} aria-label="delete">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
    );
  };

  const renderReelExportTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Reel</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Requested by</TableCell>
            <TableCell>Output (S3)</TableCell>
            <TableCell>Exporter host</TableCell>
            <TableCell>Exporter IP</TableCell>
            <TableCell>Queued at</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredReelExportItems.map((item) => {
            const isInProgress = item.status === 'in-progress';
            return (
              <TableRow key={item.jobId} hover>
                <TableCell>{item.reelName || '(unknown reel)'}</TableCell>
                <TableCell>{item.versionLabel || '—'}</TableCell>
                <TableCell>
                  {renderStatusChip(item.status)}
                  {isInProgress && item.progress && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'capitalize' }}>
                      {item.progress.stage} {Math.round(item.progress.percent || 0)}%
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{item.width}×{item.height} · {formatDuration(item.durationMs)}</TableCell>
                <TableCell>{item.companyName || item.companyId || '—'}</TableCell>
                <TableCell>{item.requestedBy || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 320, wordBreak: 'break-all' }}>{item.s3Uri || '—'}</TableCell>
                <TableCell>{isInProgress ? splitExporter(item.owner).host : '—'}</TableCell>
                <TableCell>{isInProgress ? splitExporter(item.owner).ip : '—'}</TableCell>
                <TableCell>{formatTimestamp(item.enqueuedAt)}</TableCell>
                <TableCell align="right">
                  {isInProgress ? (
                    <IconButton disabled size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton color="error" size="small" onClick={() => handleReelExportDeleteClick(item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderOpenAiTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Prompt ID</TableCell>
            <TableCell>Job ID</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Visible at</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredOpenAiItems.map((item) => {
            const isInProgress = item.status === 'in-progress';
            return (
              <TableRow key={item.jobId} hover>
                <TableCell>{item.title || '(untitled)'}</TableCell>
                <TableCell>{item.projectName || '—'}</TableCell>
                <TableCell>{renderStatusChip(item.status)}</TableCell>
                <TableCell sx={{ wordBreak: 'break-all' }}>{item.promptId || '—'}</TableCell>
                <TableCell sx={{ wordBreak: 'break-all' }}>{item.jobId}</TableCell>
                <TableCell>{item.owner || '—'}</TableCell>
                <TableCell>{formatTimestamp(item.visibleAt)}</TableCell>
                <TableCell align="right">
                  {isInProgress ? (
                    <IconButton disabled size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton color="error" size="small" onClick={() => handleOpenAiDeleteClick(item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderNotificationsTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Event type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Created by</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Server IP</TableCell>
            <TableCell>Job ID</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Created at</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredNotificationItems.map((item) => {
            const isInProgress = item.status === 'in-progress';
            return (
            <TableRow key={item.jobId} hover>
              <TableCell>{item.auditEventType || '—'}</TableCell>
              <TableCell>{item.status ? renderStatusChip(item.status) : '—'}</TableCell>
              <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>
                {item.auditEventDescription || '—'}
              </TableCell>
              <TableCell>{item.createdBy || '—'}</TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>{item.companyId || '—'}</TableCell>
              <TableCell>{item.applicationServerIp || '—'}</TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>{item.jobId}</TableCell>
              <TableCell>{item.owner || '—'}</TableCell>
              <TableCell>{formatTimestamp(item.createdAt)}</TableCell>
              <TableCell align="right">
                {isInProgress ? (
                  <IconButton disabled size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton color="error" size="small" onClick={() => handleNotificationDeleteClick(item)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderContent = () => {
    if (viewMode === 'table') {
      if (queueType === 'openai-prompt') return renderOpenAiTable();
      if (queueType === 'reel-export') return renderReelExportTable();
      return renderNotificationsTable();
    }

    const tiles = queueType === 'openai-prompt'
      ? filteredOpenAiItems.map(renderOpenAiTile)
      : queueType === 'reel-export'
        ? filteredReelExportItems.map(renderReelExportTile)
        : filteredNotificationItems.map(renderNotificationTile);

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {tiles}
      </Box>
    );
  };

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <DynamicBreadcrumbs
        items={[
          { itemPath: '/dashboard', itemLabel: 'Home' },
          { itemPath: SYSTEM_QUEUE_PATH, itemLabel: 'System Queue' },
        ]}
      />

      <Box sx={toolbarSurfaceSx}>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileMenuOpen(true)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
            }}
          >
            <FilterList />
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
          <IconButton
            color="secondary"
            onClick={() => setToolbarMenuOpen(true)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexGrow: 1,
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TextField
            label="Search Queue"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '220px', flexShrink: 1 }}
          />
          {renderActionButtons('desktop')}
        </Box>
      </Box>

      <Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Search</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {renderQueueSelector(true)}
            <TextField
              label="Search Queue"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileMenuOpen(false)} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Actions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {renderActionButtons('mobile')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {!loading && isEmpty ? (
        <Paper sx={{ borderRadius: '8px', p: 3 }}>
          <Typography align="center" color="text.secondary">
            Queue is empty.
          </Typography>
        </Paper>
      ) : (
        !loading && renderContent()
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Queue Item</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Remove this item from the selected queue? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearOpen} onClose={() => setClearOpen(false)}>
        <DialogTitle>Clear Queue</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {queueType === 'openai-prompt'
              ? 'Remove all scheduled items from the OpenAI Prompt queue? In-progress items will not be affected.'
              : queueType === 'reel-export'
                ? 'Remove all scheduled reel exports? Exports already being processed by an exporter will not be affected.'
                : 'Remove all items from the Notifications Queue? This cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setClearOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmClear} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemQueue;
